import config from "../../config";

const base = config.api.baseUrl;

const fill = (template, params) =>
  template.replace(/:([a-zA-Z_]+)/g, (_, k) => encodeURIComponent(params[k]));

const opts = (extra = {}) => ({
  credentials: "include",
  headers: { "Content-Type": "application/json" },
  ...extra,
});

const handle = async (resp) => {
  let data = null;
  try { data = await resp.json(); } catch (_) { /* not json */ }
  if (!resp.ok || (data && data.success === false)) {
    const err = new Error((data && data.error) || `HTTP ${resp.status}`);
    err.status = resp.status;
    err.data = data; // structured payloads (e.g. 409 missing_servers) reach callers
    throw err;
  }
  return data;
};

export const api = {
  listAgents: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.agentsAvailable, opts());
    return (await handle(r)).data;
  },
  createAgent: async (payload) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.agents, opts({
      method: "POST", body: JSON.stringify(payload),
    }));
    return (await handle(r)).data;
  },
  getAgent: async (agentId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.agent, { id: agentId }), opts());
    return (await handle(r)).data;
  },
  updateAgent: async (agentId, payload) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.agent, { id: agentId }), opts({
      method: "PATCH", body: JSON.stringify(payload),
    }));
    return (await handle(r)).data;
  },
  deleteAgent: async (agentId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.agent, { id: agentId }), opts({
      method: "DELETE",
    }));
    return handle(r);
  },
  listThreads: async ({ agentId, environmentId, limit = 50, beforeId } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (agentId) params.set("agent_id", agentId);
    if (environmentId) params.set("environment_id", environmentId);
    if (beforeId) params.set("before_id", beforeId);
    const r = await fetch(`${base}${config.api.endpoints.claudeAgent.threads}?${params}`, opts());
    return (await handle(r)).data;
  },
  createThread: async (agentId, title, environmentId, model, requireApproval) => {
    const body = { title };
    if (agentId) body.agent_id = agentId;
    if (environmentId) body.environment_id = environmentId;
    if (model) body.model = model;
    if (requireApproval) body.require_approval = true;
    const r = await fetch(base + config.api.endpoints.claudeAgent.threads, opts({
      method: "POST", body: JSON.stringify(body),
    }));
    return (await handle(r)).data;
  },
  listModels: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.models, opts());
    return (await handle(r)).data;
  },
  // { blocked, message } — whether this user may send right now. Carries no
  // amounts; the cap itself is not user-facing.
  usageLimit: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.usageLimit, opts());
    return (await handle(r)).data;
  },
  adminUsage: async (days = 30) => {
    const r = await fetch(
      `${base}${config.api.endpoints.claudeAgent.adminUsage}?days=${days}`, opts());
    return await handle(r); // { by_day, by_user, totals, days }
  },
  adminUserMessages: async (userEmail, { q = "", limit = 50, before = null } = {}) => {
    const params = new URLSearchParams({ user_email: userEmail, limit: String(limit) });
    if (q) params.set("q", q);
    if (before) params.set("before", before);
    const r = await fetch(
      `${base}${config.api.endpoints.claudeAgent.adminMessages}?${params}`, opts());
    return await handle(r); // { messages, next_before }
  },
  // { user_email, spent_usd, cap_usd (override or null), default_cap_usd, is_admin }
  adminGetSpendLimit: async (userEmail) => {
    const params = new URLSearchParams({ user_email: userEmail });
    const r = await fetch(
      `${base}${config.api.endpoints.claudeAgent.adminSpendLimit}?${params}`, opts());
    return (await handle(r)).data;
  },
  // capUsd: number sets this month's override (0 = unlimited), null clears it.
  adminSetSpendLimit: async (userEmail, capUsd) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.adminSpendLimit, opts({
      method: "PUT", body: JSON.stringify({ user_email: userEmail, cap_usd: capUsd }),
    }));
    return (await handle(r)).data;
  },
  getThread: async (threadId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.thread, { id: threadId }), opts());
    return (await handle(r)).data;
  },
  patchThread: async (threadId, patch) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.thread, { id: threadId }), opts({
      method: "PATCH", body: JSON.stringify(patch),
    }));
    return (await handle(r)).data;
  },
  deleteThread: async (threadId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.thread, { id: threadId }), opts({
      method: "DELETE",
    }));
    return await handle(r);
  },
  listEvents: async (threadId, { afterSeq = 0, limit = 200 } = {}) => {
    const params = new URLSearchParams({ after_seq: String(afterSeq), limit: String(limit) });
    const r = await fetch(
      `${base}${fill(config.api.endpoints.claudeAgent.threadEvents, { id: threadId })}?${params}`,
      opts()
    );
    return (await handle(r)).data;
  },
  sendMessage: async (threadId, { text, fileIds = [], attachedPaths = [], idempotencyKey } = {}) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.threadMessages, { id: threadId }),
      opts({
        method: "POST",
        body: JSON.stringify({
          text, file_ids: fileIds, attached_paths: attachedPaths,
          idempotency_key: idempotencyKey,
        }),
      })
    );
    return (await handle(r)).data;
  },
  interrupt: async (threadId) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.threadInterrupt, { id: threadId }),
      opts({ method: "POST" })
    );
    return await handle(r);
  },
  confirmTool: async (threadId, { toolUseId, result, denyMessage } = {}) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.threadToolConfirmation, { id: threadId }),
      opts({
        method: "POST",
        body: JSON.stringify({ tool_use_id: toolUseId, result, deny_message: denyMessage }),
      })
    );
    return await handle(r);
  },
  uploadFile: async (threadId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.threadFiles, { id: threadId }),
      { method: "POST", credentials: "include", body: fd }
    );
    return (await handle(r)).data;
  },
  listEnvironments: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.environments, opts());
    return (await handle(r)).data;
  },
  createEnvironment: async (name, description) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.environments, opts({
      method: "POST", body: JSON.stringify({ name, description }),
    }));
    return (await handle(r)).data;
  },
  patchEnvironment: async (envId, patch) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environment, { id: envId }), opts({
      method: "PATCH", body: JSON.stringify(patch),
    }));
    return (await handle(r)).data;
  },
  deleteEnvironment: async (envId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environment, { id: envId }), opts({
      method: "DELETE",
    }));
    return await handle(r);
  },
  getEnvironment: async (envId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environment, { id: envId }), opts());
    return (await handle(r)).data;
  },
  shareEnvironment: async (envId, { sharedWithUsers, sharedWithRoles, sharedWithEveryone } = {}) => {
    const body = {};
    if (sharedWithUsers !== undefined) body.shared_with_users = sharedWithUsers;
    if (sharedWithRoles !== undefined) body.shared_with_roles = sharedWithRoles;
    if (sharedWithEveryone !== undefined) body.shared_with_everyone = sharedWithEveryone;
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environment, { id: envId }), opts({
      method: "PATCH", body: JSON.stringify(body),
    }));
    return (await handle(r)).data;
  },
  listEnvironmentFiles: async (envId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environmentFiles, { id: envId }), opts());
    return (await handle(r)).data;
  },
  readEnvironmentFile: async (envId, path) => {
    const url = `${base}${fill(config.api.endpoints.claudeAgent.environmentFiles, { id: envId })}?path=${encodeURIComponent(path)}`;
    const r = await fetch(url, opts());
    return (await handle(r)).data;
  },
  writeEnvironmentFile: async (envId, path, content) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environmentFiles, { id: envId }), opts({
      method: "POST", body: JSON.stringify({ path, content }),
    }));
    return (await handle(r)).data;
  },
  deleteEnvironmentFile: async (envId, path) => {
    const url = `${base}${fill(config.api.endpoints.claudeAgent.environmentFiles, { id: envId })}?path=${encodeURIComponent(path)}`;
    const r = await fetch(url, opts({ method: "DELETE" }));
    return await handle(r);
  },
  uploadEnvironmentFile: async (envId, file, path, onProgress) => {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("path", path || file.name);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        base + fill(config.api.endpoints.claudeAgent.envFileUpload, { id: envId }),
      );
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (err) { reject(err); }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
        }
      };
      xhr.onerror = () => reject(new Error("network error"));
      xhr.send(form);
    });
  },
  downloadEnvironmentFileUrl: (envId, path) =>
    base
      + fill(config.api.endpoints.claudeAgent.envFileDownload, { id: envId })
      + `?path=${encodeURIComponent(path)}`,
  listSkills: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.skills, opts());
    return (await handle(r)).data;
  },
  getSkill: async (skillId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.skill, { id: skillId }), opts());
    return (await handle(r)).data;
  },
  // payload/patch are JSON-stringified whole, so a `files` array passed by
  // callers (multi-file skills) flows through to the server unchanged.
  createSkill: async (payload) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.skills, opts({
      method: "POST", body: JSON.stringify(payload),
    }));
    return (await handle(r)).data;
  },
  patchSkill: async (skillId, patch) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.skill, { id: skillId }), opts({
      method: "PATCH", body: JSON.stringify(patch),
    }));
    return (await handle(r)).data;
  },
  // Upload a .zip skill bundle as multipart/form-data. Mirrors opts()'s cookie
  // auth (credentials: "include") but omits Content-Type so the browser sets
  // the multipart boundary — same idiom as uploadFile above.
  uploadSkillZip: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(base + config.api.endpoints.claudeAgent.skills, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    return (await handle(r)).data;
  },
  setSkillActive: async (skillId, isActive) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.skillActive, { id: skillId }), opts({
      method: "PUT", body: JSON.stringify({ is_active: !!isActive }),
    }));
    return (await handle(r)).data;
  },
  deleteSkill: async (skillId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.skill, { id: skillId }), opts({
      method: "DELETE",
    }));
    return await handle(r);
  },
  listSkillTemplates: async () => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.skillTemplates, opts());
    return (await handle(r)).data;
  },
  // websocket urls
  threadStreamUrl: (threadId, lastSeq = 0) => {
    const httpBase = base.replace(/^http/, "ws");
    return `${httpBase}${fill(config.api.endpoints.claudeAgent.wsThreadStream, { id: threadId })}?last_seq=${lastSeq}`;
  },
  userStatusUrl: () => {
    const httpBase = base.replace(/^http/, "ws");
    return `${httpBase}${config.api.endpoints.claudeAgent.wsUserStatus}`;
  },
  // Fire-and-forget: pre-start the env's sandbox container so the user's first
  // message/files action doesn't eat the cold start. Errors are irrelevant.
  warmEnvironment: (envId) => {
    fetch(base + fill(config.api.endpoints.claudeAgent.environmentWarm, { id: envId }),
      opts({ method: "POST" })).catch(() => {});
  },
  listEnvironmentMemory: async (envId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.environmentMemory, { id: envId }), opts());
    return (await handle(r)).data;
  },
  listEnvMcpServers: async (envId) => {
    const r = await fetch(base + fill(config.api.endpoints.claudeAgent.envMcpServers, { id: envId }), opts());
    return (await handle(r)).data;
  },
  setMcpEnabled: async (serverUrl, environmentId, enabled) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.mcpConnectionEnabled, opts({
      method: "POST",
      body: JSON.stringify({ server_url: serverUrl, environment_id: environmentId, enabled }),
    }));
    return await handle(r);
  },
  disconnectMcp: async (serverUrl) => {
    const r = await fetch(base + config.api.endpoints.claudeAgent.mcpConnectionDisconnect, opts({
      method: "POST", body: JSON.stringify({ server_url: serverUrl }),
    }));
    return await handle(r);
  },
  // --- Scheduled tasks (per-user, per-environment) ---
  listScheduledTasks: async (envId, category = "personal") => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTasks, { id: envId }) +
        `?category=${encodeURIComponent(category)}`,
      opts());
    return (await handle(r)).data;
  },
  createScheduledTask: async (envId, body) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTasks, { id: envId }),
      opts({ method: "POST", body: JSON.stringify(body) }));
    return (await handle(r)).data;
  },
  updateScheduledTask: async (envId, taskId, body) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTask, { envId, taskId }),
      opts({ method: "PATCH", body: JSON.stringify(body) }));
    return (await handle(r)).data;
  },
  deleteScheduledTask: async (envId, taskId) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTask, { envId, taskId }),
      opts({ method: "DELETE" }));
    return await handle(r);
  },
  runScheduledTaskNow: async (envId, taskId) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTaskRun, { envId, taskId }),
      opts({ method: "POST" }));
    return await handle(r);
  },
  listScheduledTaskRuns: async (envId, taskId) => {
    const r = await fetch(
      base + fill(config.api.endpoints.claudeAgent.envScheduledTaskRuns, { envId, taskId }),
      opts());
    return (await handle(r)).data;
  },
  initiateMcpOauth: async (serverUrl, environmentId, threadId) => {
    // threadId (the open chat) lets the callback eagerly rebind it to a new
    // session carrying the new credentials — see mcp_oauth_callback.
    const body = { server_url: serverUrl, environment_id: environmentId };
    if (threadId) body.thread_id = threadId;
    const r = await fetch(base + config.api.endpoints.claudeAgent.mcpOauthInitiate, opts({
      method: "POST",
      body: JSON.stringify(body),
    }));
    return await handle(r); // { success, oauth_url, state }
  },
  // Open the popup window SYNCHRONOUSLY inside the click handler (before any
  // await) so popup blockers don't eat it — an awaited fetch first breaks the
  // user-gesture chain in most browsers. Navigate it once the URL is known.
  openOauthPopup: () =>
    window.open("about:blank", "mcp_oauth", "width=600,height=720"),
  // Drives an OAuth popup. Resolves `{serverUrl, rebound}` when our callback
  // posts back, `{closed: true}` when the window went away without posting,
  // and rejects only on an error the callback explicitly reported.
  //
  // `{closed: true}` is NOT a failure. A missing postMessage proves nothing
  // about the outcome: our callback commits the connection server-side BEFORE
  // returning the page that posts and closes, and the post can be lost anyway
  // (`window.opener` severed somewhere in the provider's navigation chain, or
  // the window closing before the opener drains the queued message event —
  // `window.close()` runs on the very next statement). Callers must ask the
  // server what actually happened; see useMcpServers.connect.
  //
  // Pass the window from openOauthPopup() as `existingPopup` to keep the
  // gesture chain; falls back to opening one itself.
  runMcpOauthPopup: (oauthUrl, serverUrl, existingPopup) => new Promise((resolve, reject) => {
    const popup = existingPopup || window.open(oauthUrl, "mcp_oauth", "width=600,height=720");
    if (!popup) { reject(new Error("Popup blocked — allow popups and retry.")); return; }
    if (existingPopup) {
      try { popup.location.href = oauthUrl; }
      catch (_) { reject(new Error("Could not open the sign-in page.")); return; }
    }
    const onMessage = (event) => {
      const d = event.data || {};
      if (d.type === "mcp_oauth_success") { cleanup(); resolve({ serverUrl: d.server_url, rebound: !!d.rebound }); }
      else if (d.type === "mcp_oauth_error") { cleanup(); reject(new Error(d.error || "OAuth failed")); }
    };
    const timer = setInterval(() => {
      if (popup.closed) { cleanup(); resolve({ closed: true }); }
    }, 800);
    function cleanup() { clearInterval(timer); window.removeEventListener("message", onMessage); try { popup.close(); } catch (_) {} }
    window.addEventListener("message", onMessage);
  }),
};
